import { Alert, Badge, Button, Card } from "react-bootstrap"
import { Link } from "react-router-dom"
import timeService from "../../services/time/timeService"
import { NotificationEntryType } from "../../Types/notificationEntryType"
import useUserRefs from "../../Hooks/UseUsersQuery/UseUserRefs"
import { useAddNewDataLogMutation, useEditNotificationEntryMutation, useGetBookingByIdQuery, useGetBookingsQuery, useGetDataLogsQuery, useGetPropertyIcalDataQuery } from "../../services/bClientApi"
import { convertStringToJSON } from "../../Helpers/Strings/stringHelpers"
import { useState } from "react"
import QuickForm from "../Forms/QuickForm/QuickForm"
import { useAppDispatch } from "../../redux/store"
import { showError, showSuccess } from "../../redux/counter/counterSlice"
import { navigateTo } from "../../redux/router/routerActions"
import canAccess from "../../services/accessService/accessService"
import PropertyTitleText from "../PropertyEntry/PropertyTitleText"
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook"

export default function NotificationEntry(props: {
    notificationData: NotificationEntryType,
    fullView?: boolean,
    customButtons?: {
        onClick: (notificationData: NotificationEntryType) => void,
        text: string,
        variant?: string,
        disabled?: boolean,
    }[]
}) {
    const dispatch = useAppDispatch()
    const [resolveMode, setResolveMode] = useState(false)

    const {
        notificationData,
        fullView: initialFullView = false,
    } = props

    const [fullView, setFullView] = useState(initialFullView)

    const userRefs = useUserRefs()
    const { myProperties } = useMyProperties()

    const [
        editNotificationEntry,
        {
            isLoading: isEditing,
            // error: editError,
        }
    ] = useEditNotificationEntryMutation()

    const [
        addDataLog,
        //  { isLoading: isAddingLog, }
    ] = useAddNewDataLogMutation()

    const {
        _id,
        userId,
        type,
        data = '',
        createdDate,
        createdBy,
        readDate,
        message,
        status,
    } = notificationData || {}

    const parsedData = convertStringToJSON(data)

    const isRead = !!readDate

    const isResolved = status && status === 'resolved'

    let bookingId = ''
    let otherBookingId = ''

    if ((type.indexOf('booking_missing_ical_source') > -1 || type.indexOf('booking_double_booking') > -1) && parsedData.bookingId) {
        bookingId = parsedData.bookingId
    }
    if (type.indexOf('booking_double_booking') > -1 && parsedData.otherBookingId) {
        otherBookingId = parsedData.otherBookingId
    }

    let airbnbLink = ''
    if (type.indexOf('ical_missing_booking_source') > -1 && parsedData.source === 'airbnb' && parsedData?.rawData?.description.indexOf('https://www.airbnb.com/') > -1) {
        // https://www.airbnb.com/hosting/reservations/details/HMN12345678
        airbnbLink = parsedData?.rawData?.description.match(/https:\/\/www\.airbnb\.com\/\S+/)?.[0] || 'noway'
    }
    let airbnbConfirmationCode = ''
    if (airbnbLink && airbnbLink.length > 0 && airbnbLink !== 'noway') {
        const possibleAirbnbConfirmation = airbnbLink.split('/').pop()
        if (possibleAirbnbConfirmation && possibleAirbnbConfirmation.length >= 6) {
            airbnbConfirmationCode = possibleAirbnbConfirmation
        }
    }

    let icalUID = ''
    if (type.indexOf('ical_missing_booking_source') > -1 && parsedData?.rawData?.uid) {
        icalUID = parsedData.rawData.uid
    }

    let vrboInboxLink = ''
    if (type.indexOf('ical_missing_booking_source') > -1 && parsedData.source === 'vrbo' && parsedData.propertyId) {
        const propertyData = myProperties[parsedData.propertyId]
        if (propertyData?.listed && propertyData.listed.length > 0) {
            const vrboListing = propertyData.listed.find((listing: any) => listing.source === 'homeaway' && listing.internalId)
            if (vrboListing) {
                vrboInboxLink = `https://www.vrbo.com/supply/inbox?propertyId=${vrboListing.internalId}`
            }
        }
    }


    const {
        data: bookingData,
    } = useGetBookingsQuery({
        bookingsQuery: {
            sourceConfirmation: airbnbConfirmationCode,
            bookingStatus: ['new', 'pending', 'confirmed']
        },
        pagination: {
            page: 1,
            limit: 1,
        }
    }, {
        skip: !fullView || !airbnbConfirmationCode,
    })
    const matchingBooking = bookingData?.docs?.[0] || {}

    const {
        data: bookingDataLogListResponse,
    } = useGetDataLogsQuery({
        dataLogsQuery: {
            dataId: bookingId,
            dataType: 'booking',
            logType: `resolve_${type}`,
        },
        pagination: {
            page: 1,
            limit: 1,
        }
    }, {
        skip: !bookingId || !fullView,
    })
    const bookingDataLogList = bookingDataLogListResponse?.docs || []

    const {
        data: icalDataLogListResponse,
    } = useGetDataLogsQuery({
        dataLogsQuery: {
            dataId: icalUID,
            dataType: 'ical',
            logType: `resolve_${type}`,
        },
        pagination: {
            page: 1,
            limit: 1,
        }
    }, {
        skip: !icalUID || !fullView,
    })
    const icalDataLogList = icalDataLogListResponse?.docs || []

    // For booking_missing_ical_source notifications, fetch booking and ical data to check for matches
    const isBookingMissingIcalSource = type.indexOf('booking_missing_ical_source') > -1
    
    const {
        data: bookingDataForIcalCheck,
    } = useGetBookingByIdQuery(bookingId, {
        skip: !fullView || !isBookingMissingIcalSource || !bookingId,
    })
    const bookingForIcalCheck = bookingDataForIcalCheck || {}

    const {
        data: icalDataResponse,
    } = useGetPropertyIcalDataQuery(
        bookingForIcalCheck?.propertyId || '',
        {
            skip: !fullView || !isBookingMissingIcalSource || !bookingForIcalCheck?.propertyId,
            refetchOnFocus: false,
        }
    )
    const icalEntries = icalDataResponse?.propertyIcalData || []

    // Check if there's a matching ical entry
    let matchingIcalEntry = null
    if (isBookingMissingIcalSource && bookingForIcalCheck && icalEntries.length > 0) {
        const bookingDateHash = timeService.createDateRangeHash(bookingForIcalCheck.checkIn, bookingForIcalCheck.checkOut)
        
        matchingIcalEntry = icalEntries.find((ical: any) => {
            const icalDateHash = timeService.createDateRangeHash(ical.checkIn, ical.checkOut)
            
            // Check if dates match
            const datesMatch = bookingDateHash === icalDateHash

            let icalSourceCofirmation = ''
            // if ical source is airbnb, try to get confirmation from rawData description
            if (ical.source.indexOf('airbnb') > -1 && ical.rawData && ical.rawData.description) {
                const possibleAirbnbConfirmation = ical.rawData.description.match(/https:\/\/www\.airbnb\.com\/hosting\/reservations\/details\/([A-Z0-9]+)/)?.[1] || ''
                if (possibleAirbnbConfirmation && possibleAirbnbConfirmation.length >= 6) {
                    icalSourceCofirmation = possibleAirbnbConfirmation
                }
            } else {
                icalSourceCofirmation = ical.sourceConfirmation || ''
            }
            
            // Check if source confirmation matches (if both have it)
            const sourceConfirmationMatch = bookingForIcalCheck.sourceConfirmation && icalSourceCofirmation
                ? bookingForIcalCheck.sourceConfirmation === icalSourceCofirmation
                : false
            
            return datesMatch || sourceConfirmationMatch
        })
    }

    const markAsRead = async (navigate: boolean = false) => {
        if (!notificationData._id) { return }
        if (!isRead) {
            const editResponse = await editNotificationEntry({
                notificationEntryId: notificationData._id,
                notificationEntryInfo: {
                    readDate: new Date(),
                }
            }).unwrap().catch(error => {
                console.error("Error marking notification as read:", error)
                return {
                    error: error.message || 'Failed to mark notification as read'
                }
            })

            if (editResponse.error) {
                dispatch(showError(editResponse.error))
            } else {
                dispatch(showSuccess("Notification marked as read"))
            }
        }

        if (navigate) {
            dispatch(navigateTo(`/notification-entry/${notificationData._id}`))
        }
    }

    const markAsUnread = async () => {
        if (!notificationData._id) { return }

        const editResponse = await editNotificationEntry({
            notificationEntryId: notificationData._id,
            notificationEntryInfo: {
                readDate: null,
            }
        }).unwrap().catch(error => {
            console.error("Error marking notification as unread:", error)
            return {
                error: error.message || 'Failed to mark notification as unread'
            }
        })

        if (editResponse.error) {
            dispatch(showError(editResponse.error))
        } else {
            dispatch(showSuccess("Notification marked as unread"))
        }

    }

    const handleNotificationResolve = async (data: any, dataId: string, dataType: string) => {
        if (!notificationData._id) { return }
        if (!data.autoResolve) {
            const addLogResponse = await addDataLog({
                dataId: dataId,
                dataType: dataType,
                note: data.resolutionInformation,
                logType: `resolve_${type}`,
                data: `{}`,
                createdBy: userId,
                createdDate: new Date(),
            }).unwrap().catch(error => {
                console.error("Error adding data log:", error)
                return {
                    error: error.message || 'Failed to add data log'
                }
            })

            if (addLogResponse.error) {
                dispatch(showError(addLogResponse.error))
                return
            }
        }

        const editResponse = await editNotificationEntry({
            notificationEntryId: notificationData._id,
            notificationEntryInfo: {
                readDate: new Date(),
                status: 'resolved'
            }
        }).unwrap().catch(error => {
            console.error("Error resolving notification:", error)
            return {
                error: error.message || 'Failed to resolve notification'
            }
        })
        if (editResponse.error) {
            dispatch(showError(editResponse.error))
        } else {
            dispatch(showSuccess("Notification resolved successfully"))
        }

    }

    // Helper function to get user display name
    const getUserDisplayName = (userId: string) => {
        const user = userRefs[userId]
        const userNameParts: string[] = []
        if (user) {
            if (user.firstName) {
                userNameParts.push(user.firstName)
            }
            if (user.lastName) {
                userNameParts.push(user.lastName)
            }
            if (userNameParts.length > 0) {
                return `${userNameParts.join(' ')} (${user.email})`
            }
            return user.email
        }
        return userId // fallback to ID if user not found
    }

    const cardStyle = {
        backgroundColor: isRead ? `#f9f9f9` : `#e8f4f8`
    }

    // if booking_double_booking, highlight in red
    if (type.indexOf('booking_double_booking') > -1 && !isResolved) {
        cardStyle.backgroundColor = `#f8d7da`
    }

    return (
        <Card
            style={cardStyle}
            className="mb-3">
            <Card.Body>
                <Card.Title>
                    {type}
                </Card.Title>

                <Card.Text className="mb-1 small">
                    {getUserDisplayName(userId)}
                </Card.Text>
                <Card.Text className="mb-3 small">
                    {timeService.getFormattedTime(createdDate)}
                </Card.Text>


                {message && (
                    <Card.Text className="mb-3">
                        {message}
                    </Card.Text>
                )}

                {fullView && (
                    <>
                        <hr />
                        <Card.Subtitle className="mb-2">
                            <b>
                                Details:
                            </b>
                        </Card.Subtitle>
                        <Card.Text className="mb-1 small">
                            <b>For:</b> {getUserDisplayName(userId)}
                        </Card.Text>
                        <Card.Text className="mb-1 small">
                            <b>Type:</b> {type}
                        </Card.Text>
                        <Card.Text className="mb-1 small">
                            <b>Created By:</b> {getUserDisplayName(createdBy)}
                        </Card.Text>
                        {data && (
                            <Card.Text className="mb-1 small">
                                <b>Data:</b> <br />
                                <pre>{JSON.stringify(parsedData, null, 2)}</pre>
                            </Card.Text>
                        )}
                        {readDate && (
                            <Card.Text className="mb-1 small">
                                <b>Read Date:</b> {timeService.getFormattedTime(readDate)}
                            </Card.Text>
                        )}

                        {/* THIS IS FOR BOOKING NOTIFICATIONS ONLY */}
                        {bookingId && (
                            <Card.Text className="mb-1 small">
                                <b>Booking ID:</b> {bookingId} <br />
                                <Link to={`/booking/${bookingId}`} className="btn btn-link p-0">
                                    View Booking
                                </Link>
                                <br />
                                
                                {/* Show matching ical entry for booking_missing_ical_source */}
                                {isBookingMissingIcalSource && matchingIcalEntry && (
                                    <Alert variant="success" className="mt-2">
                                        <strong>✓ Matching iCal Entry Found</strong>
                                        <br />
                                        <small>
                                            <b>Summary:</b> {matchingIcalEntry.summary}<br />
                                            <b>Source:</b> {matchingIcalEntry.source}<br />
                                            <b>Check In:</b> {timeService.getFormattedTime(matchingIcalEntry.checkIn)}<br />
                                            <b>Check Out:</b> {timeService.getFormattedTime(matchingIcalEntry.checkOut)}
                                            {matchingIcalEntry.sourceConfirmation && (
                                                <>
                                                    <br />
                                                    <b>Confirmation:</b> {matchingIcalEntry.sourceConfirmation}
                                                </>
                                            )}
                                        </small>
                                    </Alert>
                                )}
                                {isBookingMissingIcalSource && !matchingIcalEntry && icalEntries.length > 0 && (
                                    <Alert variant="warning" className="mt-2">
                                        <small>No matching iCal entry found ({icalEntries.length} entries checked)</small>
                                    </Alert>
                                )}
                                
                                {otherBookingId && (
                                    <>
                                        <b>Other Booking ID:</b> {otherBookingId} <br />
                                        <Link to={`/booking/${otherBookingId}`} className="btn btn-link p-0">
                                            View Booking
                                        </Link>
                                    </>
                                )}

                                {/* resolve mode button */}
                                {!isResolved && (
                                    <div className="my-2">
                                        {!resolveMode && (
                                            <>
                                                <Button
                                                    className="mt-2"
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        if (bookingDataLogList.length > 0) {
                                                            handleNotificationResolve({ autoResolve: true }, bookingId, 'booking')
                                                            return
                                                        }
                                                        setResolveMode(true)
                                                    }}>
                                                    Resolve
                                                </Button>
                                                {bookingDataLogList.length > 0 && (
                                                    <div className="mt-2">
                                                        <b>Previous Resolutions:</b>
                                                        <ul>
                                                            {bookingDataLogList.map((log: any) => (
                                                                <li key={log._id}>
                                                                    {timeService.getFormattedTime(log.createdDate)} - {log.note}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {resolveMode && (
                                            <QuickForm
                                                initialData={{
                                                    resolutionInformation: '',
                                                }}
                                                onSubmit={(data) => {
                                                    console.log('Resolved data:', data)
                                                    handleNotificationResolve(data, bookingId, 'booking')
                                                    setResolveMode(false)
                                                }}
                                                submitText="Resolve"
                                                cancelText="Cancel"
                                                onCancel={() => {
                                                    setResolveMode(false)
                                                }}
                                                fields={[
                                                    {
                                                        fieldName: 'resolutionInformation',
                                                        fieldLabel: 'Resolution Information',
                                                        fieldType: 'textarea',
                                                        placeholder: 'Enter resolution information here...',
                                                    }
                                                ]}
                                            />

                                        )}
                                    </div>
                                )}
                            </Card.Text>
                        )}

                        {/* THIS IS FOR AIRBNB ICAL NOTIFICATIONS ONLY */}
                        {airbnbLink && (
                            <Card.Text className="mb-3 small">
                                <b>Airbnb Link:</b> {airbnbLink} <br />
                                <a href={airbnbLink} target="_blank" rel="noopener noreferrer">
                                    View Info
                                </a>
                            </Card.Text>
                        )}

                        {/* THIS IS FOR VRBO ICAL NOTIFICATIONS ONLY */}
                        {vrboInboxLink && (
                            <Card.Text className="mb-3 small">
                                <b>VRBO Inbox:</b> {vrboInboxLink} <br />
                                <a href={vrboInboxLink} target="_blank" rel="noopener noreferrer">
                                    View VRBO Inbox
                                </a>
                            </Card.Text>
                        )}

                        {matchingBooking && matchingBooking._id && (
                            <Card.Text className="mb-3 small">
                                <b>Matching Booking Found:</b>
                                <br />
                                <b>Property:</b> <PropertyTitleText propertyId={matchingBooking.propertyId} /> <br />
                                <b>Guest Name:</b> {matchingBooking.contactInfo?.firstName} {matchingBooking.contactInfo?.lastName} <br />
                                <b>Check In:</b> {timeService.getFormattedTime(matchingBooking.checkIn)} <br />
                                <b>Check Out:</b> {timeService.getFormattedTime(matchingBooking.checkOut)} <br />
                                <small>
                                    <b>Date Booked:</b> {timeService.getFormattedTime(matchingBooking.dateBooked)} <br />
                                </small>
                                <Link to={`/booking/${matchingBooking._id}`}>
                                    View Booking
                                </Link>
                            </Card.Text>
                        )}

                        {icalUID && (
                            <Card.Text className="mb-3 small">
                                <b>iCal UID:</b> {icalUID} <br />
                                {!isResolved && (
                                    <div className="my-2">
                                        {!resolveMode && (
                                            <>
                                                <Button
                                                    className="mt-2"
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => {
                                                        if (icalDataLogList.length > 0) {
                                                            handleNotificationResolve({ autoResolve: true }, icalUID, 'ical')
                                                            return
                                                        }
                                                        setResolveMode(true)
                                                    }}
                                                >
                                                    Resolve
                                                </Button>
                                            </>
                                        )}
                                        {resolveMode && (
                                            <QuickForm
                                                initialData={{
                                                    resolutionInformation: '',
                                                }}
                                                onCancel={() => {
                                                    setResolveMode(false)
                                                }}
                                                fields={[
                                                    {
                                                        fieldName: 'resolutionInformation',
                                                        fieldLabel: 'Resolution Information',
                                                        fieldType: 'textarea',
                                                        placeholder: 'Enter resolution information here...',
                                                    }
                                                ]}
                                                submitText="Resolve"
                                                cancelText="Cancel"
                                                onSubmit={(data) => {
                                                    console.log('Resolved data:', data)
                                                    handleNotificationResolve(data, icalUID, 'ical')
                                                    setResolveMode(false)
                                                }}
                                            />
                                        )}
                                        {icalDataLogList.length > 0 && (
                                            <div className="mt-2">
                                                <b>Previous Resolutions:</b>
                                                <ul>
                                                    {icalDataLogList.map((log: any) => (
                                                        <li key={log._id}>
                                                            {timeService.getFormattedTime(log.createdDate)} - {log.note}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card.Text>
                        )}


                        <hr />
                    </>
                )}

                {/* THIS IS FOR AIRBNB ICAL NOTIFICATIONS ONLY */}
                {!fullView && airbnbLink && (
                    <Card.Text className="mb-3 small">
                        <b>Airbnb Link:</b> {airbnbLink} <br />
                        <a href={airbnbLink} target="_blank" rel="noopener noreferrer">
                            View Info
                        </a>
                    </Card.Text>
                )}

                {/* THIS IS FOR VRBO ICAL NOTIFICATIONS ONLY */}
                {!fullView && vrboInboxLink && (
                    <Card.Text className="mb-3 small">
                        <b>VRBO Inbox:</b> {vrboInboxLink} <br />
                        <a href={vrboInboxLink} target="_blank" rel="noopener noreferrer">
                            View VRBO Inbox
                        </a>
                    </Card.Text>
                )}

            </Card.Body>
            <Card.Footer>
                <div className="notification-info-badges">
                    <Badge
                        bg={typeColors[type] ? typeColors[type].bg : 'secondary'}
                        text={typeColors[type] ? typeColors[type].text : 'light'}
                        className="me-2 mb-2">
                        {type}
                    </Badge>

                    {/* status badge */}
                    {status && (
                        <Badge
                            className="me-2 mb-2">
                            {status}
                        </Badge>
                    )}

                    <Badge
                        bg={isRead ? 'success' : 'warning'}
                        text={isRead ? 'light' : 'dark'}
                        className="me-2 mb-2">
                        {isRead ? 'Read' : 'Unread'}
                    </Badge>
                </div>

                {!fullView && (
                    <Link className="btn btn-primary btn-sm me-2 mb-2" to={`/notification-entry/${_id}`}>View</Link>
                )}
                {/* {!fullView && (
                    <Button
                        className="me-2 mb-2"
                        size="sm"
                        variant="success"
                        disabled={isEditing}
                        onClick={() => {
                            markAsRead(true)
                        }}>
                        View
                    </Button>
                )} */}
                {/* canAccess('route', 'edit-notification-entry') && (
                <Link className="btn btn-secondary btn-sm me-2 mb-2" to={`/edit-notification-entry/${_id}`}>Edit</Link>
                ) */}
                {canAccess('route', 'delete-notification-entry') && (
                    <Link className="btn btn-danger btn-sm me-2 mb-2" to={`/delete-notification-entry/${_id}`}>Delete</Link>
                )}
                {isRead ? (
                    <Button
                        className="me-2 mb-2"
                        variant="secondary"
                        size="sm"
                        disabled={isEditing}
                        onClick={markAsUnread}>
                        Mark as Unread
                    </Button>
                ) : (
                    <Button
                        className="me-2 mb-2"
                        variant="primary"
                        size="sm"
                        disabled={isEditing}
                        onClick={() => markAsRead()}>
                        Mark as Read
                    </Button>
                )}

                {!initialFullView && (
                    <Button
                        className="me-2 mb-2"
                        variant="secondary"
                        size="sm"
                        disabled={isEditing}
                        onClick={() => {
                            setFullView(!fullView)
                        }}>
                        {fullView ? 'Hide Details' : 'Show Details'}
                    </Button>
                )}

                {props.customButtons && props.customButtons.map((button, index) => (
                    <Button
                        key={index}
                        className="me-2 mb-2"
                        variant={button.variant || 'primary'}
                        size="sm"
                        disabled={button.disabled || isEditing}
                        onClick={() => button.onClick(notificationData)}
                    >
                        {button.text}
                    </Button>
                ))}

            </Card.Footer>
        </Card >
    )
}

const typeColors: any = {
    'info': { bg: 'info', text: 'light' },
    'warning': { bg: 'warning', text: 'dark' },
    'error': { bg: 'danger', text: 'light' },
    'success': { bg: 'success', text: 'light' },
}
